import type { ApiErrorBody, ClientApp } from '@kts/shared';

/**
 * Keytosell API klienti (veb va mobil ilovalar uchun umumiy).
 *
 *  - access token xotirada saqlanadi (XSS da localStorage'dan o'g'irlanmasin);
 *  - veb: refresh token httpOnly cookie da (JS ko'rmaydi);
 *  - mobil (Capacitor): refresh token `TokenStorage` orqali (Preferences/Keychain);
 *  - 401 bo'lsa bir marta refresh qilib so'rov qaytariladi; parallel so'rovlar bitta refresh'ni kutadi.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export interface TokenStorage {
  get(): Promise<string | null> | string | null;
  set(token: string | null): Promise<void> | void;
}

export const localTokenStorage = (key = 'kts.rt'): TokenStorage => ({
  get: () => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (t) => {
    try {
      if (t) localStorage.setItem(key, t);
      else localStorage.removeItem(key);
    } catch {
      /* e'tiborsiz */
    }
  },
});

export interface Tokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface ApiOptions {
  baseUrl: string;
  client: ClientApp;
  /** Faqat mobil ilovalar uchun (veb cookie ishlatadi). */
  storage?: TokenStorage;
  onAuthLost?: () => void;
  /** Joriy til — har so'rovga `Accept-Language` sifatida qo'shiladi (server xabarlari, hujjatlar). */
  locale?: () => string;
}

type Query = Record<string, string | number | boolean | null | undefined | string[]>;

export interface RequestOpts {
  query?: Query;
  body?: unknown;
  signal?: AbortSignal;
  /** Javobni Blob sifatida (eksport, PDF). */
  blob?: boolean;
  /** 401 da refresh qilinmasin (login/refresh o'zi). */
  noRefresh?: boolean;
}

function qs(query?: Query): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function createApi(opts: ApiOptions) {
  let accessToken: string | null = null;
  let refreshing: Promise<boolean> | null = null;
  const useCookie = opts.client === 'web';

  async function raw(method: string, path: string, o: RequestOpts = {}): Promise<Response> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    const lang = opts.locale?.();
    if (lang) headers['Accept-Language'] = lang;
    let body: BodyInit | undefined;
    if (o.body instanceof FormData) body = o.body;
    else if (o.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(o.body);
    }
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    return fetch(`${opts.baseUrl}/api/v1${path}${qs(o.query)}`, {
      method,
      headers,
      body,
      signal: o.signal,
      credentials: useCookie ? 'include' : 'omit',
    });
  }

  async function parseError(res: Response): Promise<ApiError> {
    try {
      const j = (await res.json()) as ApiErrorBody;
      return new ApiError(res.status, j.error?.code ?? 'ERROR', j.error?.message ?? res.statusText, j.error?.details);
    } catch {
      return new ApiError(res.status, 'ERROR', res.statusText || 'Tarmoq xatosi');
    }
  }

  async function doRefresh(): Promise<boolean> {
    try {
      const stored = useCookie ? undefined : ((await opts.storage?.get()) ?? undefined);
      if (!useCookie && !stored) return false;
      const res = await raw('POST', '/auth/refresh', { body: stored ? { refreshToken: stored } : {}, noRefresh: true });
      if (!res.ok) return false;
      const t = (await res.json()) as Tokens;
      accessToken = t.accessToken;
      if (!useCookie) await opts.storage?.set(t.refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  /** Bir vaqtda faqat bitta refresh. */
  function refresh(): Promise<boolean> {
    refreshing ??= doRefresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  async function request<T>(method: string, path: string, o: RequestOpts = {}): Promise<T> {
    let res: Response;
    try {
      res = await raw(method, path, o);
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      throw new ApiError(0, 'NETWORK', 'Server bilan aloqa yo‘q. Internetni tekshiring');
    }
    if (res.status === 401 && !o.noRefresh) {
      if (await refresh()) res = await raw(method, path, o);
      if (res.status === 401) {
        accessToken = null;
        opts.onAuthLost?.();
      }
    }
    if (!res.ok) throw await parseError(res);
    if (o.blob) return (await res.blob()) as T;
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  async function login(login: string, password: string, orgId?: string): Promise<void> {
    const t = await request<Tokens>('POST', '/auth/login', { body: { login, password, client: opts.client, orgId }, noRefresh: true });
    accessToken = t.accessToken;
    if (!useCookie) await opts.storage?.set(t.refreshToken);
  }

  async function logout(): Promise<void> {
    try {
      const stored = useCookie ? undefined : ((await opts.storage?.get()) ?? undefined);
      await request('POST', '/auth/logout', { body: stored ? { refreshToken: stored } : {}, noRefresh: true });
    } catch {
      /* baribir chiqamiz */
    }
    accessToken = null;
    if (!useCookie) await opts.storage?.set(null);
  }

  async function switchOrg(orgId: string): Promise<void> {
    const t = await request<Tokens>('POST', '/auth/switch-org', { body: { orgId } });
    accessToken = t.accessToken;
    if (!useCookie) await opts.storage?.set(t.refreshToken);
  }

  return {
    get: <T>(path: string, query?: Query, signal?: AbortSignal) => request<T>('GET', path, { query, signal }),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, { body }),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body }),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, { body }),
    del: <T>(path: string) => request<T>('DELETE', path),
    blob: (path: string, query?: Query) => request<Blob>('GET', path, { query, blob: true }),
    upload: <T>(path: string, file: File, query?: Query) => {
      const fd = new FormData();
      fd.append('file', file);
      return request<T>('POST', path, { body: fd, query });
    },
    request,
    login,
    logout,
    switchOrg,
    /** Ilova ochilganda: sessiya bormi (refresh orqali access token olish). */
    restore: () => refresh(),
    hasToken: () => accessToken !== null,
  };
}

export type Api = ReturnType<typeof createApi>;
